<template>
    <div class="nacex-table-container">
        <div class="p-d-flex p-jc-between p-mb-3">
            <h3>Histórico de Expedições</h3>
            <div>
                <Button
                    label="Novo Envio/Recolha"
                    icon="pi pi-plus"
                    class="p-button p-button-sm"
                    @click="openForm"
                    v-if="patId"
                />
                <Button
                    label="Atualizar Lista"
                    icon="pi pi-refresh"
                    class="p-button-text p-button-sm"
                    @click="fetchShipments"
                    :loading="loading"
                />
            </div>
        </div>

        <DataTable :value="shipments"
            :filters.sync="filters"
            filterDisplay="row"
            :loading="loading"
            class="p-datatable-sm"
            responsiveLayout="scroll"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            :lazy="true"
            :totalRecords="totalRecords"
            :first="(currentPage - 1) * perPage"
            @filter="fetchShipments"
            @sort="fetchShipments"
            @page="fetchShipments"
            :expandedRows.sync="expandedRows"
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse">
            <template #empty>
                Nenhuma expedição ou recolha encontrada para este PAT.
            </template>

            <Column :expander="true" />

            <Column field="type" header="Tipo" filterField="type">
                <template #body="slotProps">
                    <span :class="'badge badge-' + slotProps.data.type">
                        {{ getTypeLabel(slotProps.data.type) }}
                    </span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="internal_dossier_no" header="Dossier Interno" filterField="internal_dossier_no">
                <template #body="slotProps">
                    {{ slotProps.data.internal_dossier_type || ' - ' }} -
                    {{ slotProps.data.internal_dossier_no || '-' }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column v-if="!patId" field="pat_id" header="PAT" filterField="pat_id">
                <template #body="slotProps">
                    {{ slotProps.data.pat_id }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="requested_date" header="Data Solicitada" filterField="requested_date">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.requested_date) }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <v-date-picker
                        mode="date"
                        v-model="filterModel.value"
                        @input="onDateFilterChange($event, filterModel, filterCallback)"
                        :masks="{
                            input: 'DD-MM-YYYY',
                            data: 'DD-MM-YYYY'
                        }"
                        >
                        <template v-slot="{ inputValue, inputEvents }">
                            <input
                            class="p-column-filter p-inputtext p-component"
                            :value="inputValue"
                            v-on="inputEvents"
                            />
                        </template>
                    </v-date-picker>
                </template>
            </Column>

            <Column field="shipment_code" header="Nº Guia/Recolha" filterField="shipment_code">
                <template #body="slotProps">
                    <span style="font-weight: bold; font-family: monospace;">
                        {{ slotProps.data.shipment_code }}
                    </span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="status" header="Estado" filterField="status">
                <template #body="slotProps">
                    <span :class="'status-badge status-' + getStatusClass(slotProps.data.status)">
                        {{ slotProps.data.status }}
                    </span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText
                    v-model="filterModel.value"
                    class="p-column-filter"
                    @input="filterCallback()"
                    />
                </template>
            </Column>

            <Column field="created_at" header="Data de Criação" filterField="created_at">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.created_at) }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <v-date-picker
                        mode="date"
                        v-model="filterModel.value"
                        @input="onDateFilterChange($event, filterModel, filterCallback)"
                        :masks="{
                            input: 'DD-MM-YYYY',
                            data: 'DD-MM-YYYY'
                        }"
                        >
                        <template v-slot="{ inputValue, inputEvents }">
                            <input
                            class="p-column-filter p-inputtext p-component"
                            :value="inputValue"
                            v-on="inputEvents"
                            />
                        </template>
                    </v-date-picker>
                </template>
            </Column>

            <Column header="Ações" style="width: 180px; text-align: center">
                <template #body="slotProps">
                    <div class="actions-group">
                        <Button
                            v-if="slotProps.data.tracking_url"
                            icon="pi pi-external-link"
                            class="p-button-rounded p-button-text p-button-info p-mr-1"
                            v-tooltip.top="'Rastrear na Nacex'"
                            @click="openTracking(slotProps.data.tracking_url)"
                        />

                        <Button
                            icon="pi pi-print"
                            class="p-button-rounded p-button-text p-button-primary p-mr-1"
                            v-tooltip.top="'Gerar PDF da Etiqueta'"
                            :disabled="!slotProps.data.label_base64"
                            @click="generatePdf(slotProps.data)"
                        />

                        <Button
                            icon="pi pi-sync"
                            class="p-button-rounded p-button-text p-button-secondary p-mr-1"
                            v-tooltip.top="'Atualizar Estado'"
                            :disabled="isCanceled(slotProps.data)"
                            @click="syncStatus(slotProps.data)"
                        />

                        <Button
                            icon="pi pi-times-circle"
                            class="p-button-rounded p-button-text p-button-danger"
                            v-tooltip.top="'Cancelar'"
                            :disabled="isCanceled(slotProps.data)"
                            @click="confirmCancel(slotProps.data)"
                        />
                    </div>
                </template>
            </Column>

            <template #expansion="slotProps">
                <div class="p-4 expanded-row-content" v-if="slotProps.data.nacex_details">

                    <div class="p-grid p-mb-3">
                        <div class="p-col-12 p-md-6">
                            <h5 class="text-primary p-mb-2">
                                <i class="pi pi-building p-mr-2"></i>
                                {{ slotProps.data.type === 'pickup' ? 'Local de Recolha' : 'Remetente' }}
                            </h5>
                            <div class="info-card" v-if="slotProps.data.type === 'pickup' && slotProps.data.nacex_details.pickup_location">
                                <strong>{{ slotProps.data.nacex_details.pickup_location.name }}</strong><br>
                                {{ slotProps.data.nacex_details.pickup_location.address }}<br>
                                {{ slotProps.data.nacex_details.pickup_location.zip }} {{ slotProps.data.nacex_details.pickup_location.city }}<br>
                                <small v-if="slotProps.data.nacex_details.pickup_location.phone">
                                    <i class="pi pi-phone"></i> {{ slotProps.data.nacex_details.pickup_location.phone }}
                                </small>
                            </div>
                            <div class="info-card" v-else-if="slotProps.data.nacex_details.sender">
                                <strong>{{ slotProps.data.nacex_details.sender.name }}</strong><br>
                                {{ slotProps.data.nacex_details.sender.address }}<br>
                                {{ slotProps.data.nacex_details.sender.zip }} {{ slotProps.data.nacex_details.sender.city }}<br>
                                <small v-if="slotProps.data.nacex_details.sender.phone">
                                    <i class="pi pi-phone"></i> {{ slotProps.data.nacex_details.sender.phone }}
                                </small>
                            </div>
                        </div>

                        <div class="p-col-12 p-md-6" v-if="slotProps.data.type !== 'pickup'">
                            <h5 class="text-primary p-mb-2">
                                <i class="pi pi-home p-mr-2"></i>Destinatário
                            </h5>
                            <div class="info-card" v-if="slotProps.data.nacex_details.recipient">
                                <strong>{{ slotProps.data.nacex_details.recipient.name }}</strong><br>
                                {{ slotProps.data.nacex_details.recipient.address }}<br>
                                {{ slotProps.data.nacex_details.recipient.zip }} {{ slotProps.data.nacex_details.recipient.city }}<br>
                                <small v-if="slotProps.data.nacex_details.recipient.phone">
                                    <i class="pi pi-phone"></i> {{ slotProps.data.nacex_details.recipient.phone }}
                                </small>
                            </div>
                        </div>

                        <div class="p-col-12 p-md-6" v-else>
                            <h5 class="text-primary p-mb-2">
                                <i class="pi pi-info-circle p-mr-2"></i>Detalhes da Recolha
                            </h5>
                            <div class="info-card">
                                <strong>Nº Nacex:</strong> {{ slotProps.data.nacex_details.pickup_number }}<br>
                                <strong>Data:</strong> {{ slotProps.data.nacex_details.pickup_date }}<br>
                                <template v-if="slotProps.data.nacex_details.packages">
                                    <strong>Volumes:</strong> {{ slotProps.data.nacex_details.packages }} ({{ slotProps.data.nacex_details.weight }} kg)<br>
                                </template>
                                <strong v-if="slotProps.data.nacex_details.service">Serviço:</strong> {{ slotProps.data.nacex_details.service }}
                                <strong v-if="slotProps.data.nacex_details.requester">Solicitante:</strong> {{ slotProps.data.nacex_details.requester }}
                            </div>
                        </div>
                    </div>

                    <div v-if="slotProps.data.nacex_details.history && slotProps.data.nacex_details.history.length > 0">
                        <h5 class="p-mb-2"><i class="pi pi-list p-mr-2"></i>Histórico de Estados</h5>
                        <DataTable :value="slotProps.data.nacex_details.history" class="p-datatable-sm history-table">
                            <Column field="datetime" header="Data / Hora"></Column>
                            <Column field="status" header="Estado">
                                <template #body="historyProps">
                                    <span style="font-weight: 600;">{{ historyProps.data.status }}</span>
                                </template>
                            </Column>
                            <Column field="details" header="Detalhes"></Column>
                            <Column field="agency" header="Agência"></Column>
                        </DataTable>
                    </div>
                </div>

                <div v-else-if="slotProps.data.loadingDetails" class="p-text-center p-p-4">
                    <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
                    <p class="p-mt-2">A carregar detalhes da NACEX...</p>
                </div>

                <div v-else class="p-text-center p-p-4">
                    <i class="pi pi-exclamation-circle" style="font-size: 2rem; color: #ff9800;"></i>
                    <p class="p-mt-2">Não foi possível carregar os detalhes.</p>
                </div>
            </template>
        </DataTable>

        <Dialog header="Confirmar Cancelamento" :visible.sync="showCancelDialog" :style="{width: '400px'}" modal>
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem; color: #fbc02d" />
                <span>Tem a certeza que deseja cancelar esta operação na NACEX?</span>
                <p class="p-mt-2 text-muted"><small>Nota: Se a encomenda já estiver em trânsito, o cancelamento falhará.</small></p>
            </div>
            <template #footer>
                <Button label="Não" icon="pi pi-times" class="p-button-text" @click="showCancelDialog = false" />
                <Button label="Sim, Cancelar" icon="pi pi-check" class="p-button-danger" :loading="canceling" @click="cancelShipment" />
            </template>
        </Dialog>

        <Dialog
            header="Etiqueta Gerada"
            :visible.sync="showLabelDialog"
            :style="{width: '50vw'}"
            modal
            :closable="false">
            <div class="p-text-center" style="text-align: center;">
                <p class="p-mb-3" style="font-size: 1.1rem; margin-bottom: 1rem;">
                    <i class="pi pi-check-circle" style="color: green; font-size: 1.5rem;"></i>
                    Operação registada com sucesso! <br> <strong>ID: {{ labelDialog.createdShipmentId }}</strong>
                </p>

                <div v-if="labelDialog.pdfBase64">
                    <Button label="Imprimir Etiqueta" icon="pi pi-print" @click="downloadPdf" />
                    <br/><br/>
                    <iframe :src="labelDialog.pdfData" width="100%" height="450px" style="border: 1px solid #ccc;"></iframe>
                </div>
                <div v-else>
                    <p>A recolha foi agendada. O estafeta levará a etiqueta.</p>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    @click="closeGeneratedLabel"
                    class="p-button-secondary"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import shipmentsService from '@/services/shipments.service';
import { FilterMatchMode } from "primevue/api";

export default {
    name: 'NacexShippingTable',
    props: {
        patId: { type: Number, required: false },
        refreshTrigger: { type: Boolean, default: false }
    },
    watch: {
        refreshTrigger() {
            this.fetchShipments();
        }
    },
    data() {
        return {
            loading: false,
            totalRecords: 0,
            currentPage: 1,
            perPage: 50,
            shipments: [],

            showCancelDialog: false,
            selectedShipment: null,
            canceling: false,
            expandedRows: [],
            showLabelDialog: false,
            labelDialog: {
                pdfData: null,
                createdShipmentId: null,
                pdfBase64: null,
            },
            filters: {
                type: { value: null, matchMode: FilterMatchMode.CONTAINS },
                internal_dossier_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
                pat_id: { value: null, matchMode: FilterMatchMode.EQUALS },
                requested_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
                shipment_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
                status: { value: null, matchMode: FilterMatchMode.CONTAINS },
                created_at: { value: null, matchMode: FilterMatchMode.DATE_IS }
            },
        };
    },
    mounted() {
        this.fetchShipments();
    },
    methods: {
        async onRowExpand(event) {
            const shipment = event.data;

            this.$set(shipment, 'loadingDetails', true);
            this.$set(shipment, 'nacex_details', null);

            this.expandedRows = [shipment];

            try {
                const response = await this.getShipmentById(shipment.id);

                if (response && response.nacex_details) {
                    this.$set(shipment, 'nacex_details', response.nacex_details);
                    if (response.data && response.data.status !== shipment.status) {
                        shipment.status = response.data.status;
                    }
                }
            } catch (error) {
                console.error("Erro no expand:", error);
            } finally {
                shipment.loadingDetails = false;
            }
        },

        onRowCollapse(event) {
            this.expandedRows = this.expandedRows.filter(row => row.id !== event.data.id);
        },
        openForm() {
            this.$emit('openForm');
        },
        buildQueryParams(page = 1) {
            const params = new URLSearchParams();

            params.append('page', page);

            for (const [key, filter] of Object.entries(this.filters)) {
                if (filter.value !== null && filter.value !== '') {
                params.append(`filters[${key}]`, filter.value);
                }
            }

            return params;
        },
        async fetchShipments(event = {}) {
            this.loading = true;
            try {
                let response = null

                if (event.page !== undefined) {
                    this.currentPage = event.page + 1;
                } else if (event.filters !== undefined || event.sortField !== undefined) {
                    this.currentPage = 1;
                }

                if (event.rows !== undefined) {
                    this.perPage = event.rows;
                }

                const params = this.buildQueryParams(this.currentPage);
                params.append('per_page', this.perPage);

                if (this.patId) {
                    response = await shipmentsService.getShipmentsByPat(this.patId, params);
                } else {
                    response = await shipmentsService.getAllShipments(params);
                }

                this.shipments = response.data;
                this.totalRecords = response.meta ? response.meta.total : response.total;
                this.currentPage = response.meta ? response.meta.current_page : response.current_page;
            } catch (error) {
                console.error("Erro ao carregar expedições", error);
                this.$toast.add({severity:'error', summary: 'Erro', detail:'Não foi possível carregar o histórico.', life: 3000});
            } finally {
                this.loading = false;
            }
        },

        async getShipmentById(id) {
            try {
                const response = await shipmentsService.getShipmentById(id);
                if (response && response.data) {
                    return response.data;
                } else {
                    throw new Error("Resposta inválida");
                }
            } catch (error) {
                console.error("Erro ao obter detalhes da expedição", error);
                this.$toast.add({severity:'error', summary: 'Erro', detail:'Não foi possível obter detalhes da expedição.', life: 3000});
                return null;
            }
        },

        async syncStatus(shipment) {
            this.loading = true;
            try {
                const response = await shipmentsService.syncShipmentStatus(shipment.id);
                if (response.success) {
                    this.$toast.add({severity:'success', summary: 'Atualizado', detail:`Novo estado: ${response.new_status}`, life: 3000});
                    this.fetchShipments();
                }
            } catch (error) {
                this.$toast.add({severity:'error', summary: 'Erro', detail:'Falha ao sincronizar estado.', life: 3000});
            } finally {
                this.loading = false;
            }
        },

        confirmCancel(shipment) {
            this.selectedShipment = shipment;
            this.showCancelDialog = true;
        },

        async cancelShipment() {
            if (!this.selectedShipment) return;
            this.canceling = true;

            try {
                const response = await shipmentsService.cancelShipment(this.selectedShipment.id);

                if (response.success) {
                    this.$toast.add({severity:'success', summary: 'Cancelado', detail:'Operação cancelada com sucesso.', life: 3000});
                    this.showCancelDialog = false;
                    this.fetchShipments();
                }
            } catch (error) {
                const msg = error.response?.data?.error || 'Erro desconhecido';
                this.$toast.add({severity:'error', summary: 'Erro', detail: msg, life: 5000});
            } finally {
                this.canceling = false;
            }
        },

        openTracking(url) {
            if (url) window.open(url, '_blank');
        },

        getTypeLabel(type) {
            const map = {
                'shipment': 'Envio',
                'pickup': 'Recolha',
                'return': 'Devolução'
            };
            return map[type] || type;
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-PT');
        },

        isCanceled(shipment) {
            return shipment.status === 'CANCELED' || shipment.status === 'ANULADO';
        },

        getStatusClass(status) {
            const normalized = (status || '').toString().trim().toUpperCase();

            if (['CREATED', 'SCHEDULED', 'PENDIENTE', 'NOTIFICADO', 'RECOGIDO'].includes(normalized)) {
                return 'info';
            }

            if ([
                'EN TRANSITO',
                'TRANSITO',
                'EN REPARTO',
                'REPARTO',
                'ASIGNADA A MENSAJERO PARA RECOGER',
                'DISP.P.E.'
            ].includes(normalized)) {
                return 'warning';
            }

            if (['ENTREGADO', 'OK', 'RECOGIDA'].includes(normalized)) {
                return 'success';
            }

            if ([
                'CANCELED',
                'ANULADO',
                'ANULADA',
                'INCIDENCIA',
                'SOL SIN OK',
                'RECHAZADA'
            ].includes(normalized)) {
                return 'danger';
            }
            return 'secondary';
        },

        generatePdf(shipment) {
            if (shipment.label_base64) {
                this.labelDialog.pdfBase64 = shipment.label_base64;
                this.labelDialog.pdfData = 'data:application/pdf;base64,' + shipment.label_base64;
                this.labelDialog.createdShipmentId = shipment.shipment_code;
                this.showLabelDialog = true;
            }
        },

        closeGeneratedLabel() {
            this.showLabelDialog = false;
            this.labelDialog.pdfBase64 = null;
            this.labelDialog.pdfData = null;
            this.labelDialog.createdShipmentId = null;
        },

        downloadPdf() {
            if (!this.labelDialog.pdfBase64) return;

            const link = document.createElement('a');
            link.href = this.labelDialog.pdfData;
            link.download = `etiqueta_${this.labelDialog.createdShipmentId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        onDateFilterChange(date, filterModel, filterCallback) {
            if (date) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();

                filterModel.value = `${day}-${month}-${year}`;
            } else {
                filterModel.value = null;
            }

            filterCallback();
        },
    }
}
</script>

<style scoped>
.nacex-table-container {
    margin-top: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 4px;
    border: 1px solid #dee2e6;
}

.badge {
    padding: 0.25em 0.6em;
    font-size: 0.85em;
    font-weight: 700;
    color: #fff;
    border-radius: 0.25rem;
    text-transform: uppercase;
}
.badge-shipment { background-color: #007bff; }
.badge-pickup { background-color: #6610f2; }
.badge-return { background-color: #fd7e14; }

.status-badge {
    padding: 0.25em 0.6em;
    font-size: 0.85em;
    border-radius: 10px;
    font-weight: 600;
}
.status-info { background-color: #e3f2fd; color: #0d47a1; }
.status-warning { background-color: #fff3e0; color: #e65100; }
.status-success { background-color: #e8f5e9; color: #1b5e20; }
.status-danger { background-color: #ffebee; color: #b71c1c; }
.status-secondary { background-color: #f5f5f5; color: #616161; }

.actions-group {
    display: flex;
    justify-content: center;
}

.text-muted {
    color: #6c757d;
}
.expanded-row-content {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    margin: 0.5rem 0;
    padding: 10px;
}

.text-primary {
    color: #0056b3;
    font-size: 1rem;
    font-weight: 600;
}

.info-card {
    background: white;
    padding: 1rem;
    border-radius: 4px;
    border: 1px solid #dee2e6;
    font-size: 0.9rem;
    line-height: 1.5;
}

.history-table {
    border: 1px solid #dee2e6;
}

.p-datatable-sm .p-datatable-tbody > tr > td {
    padding: 0.5rem;
}
</style>